import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ReplicateContextType {
    // Replicate mode state
    replicateMode: boolean;
    setReplicateMode: (mode: boolean) => void;

    // Selected properties to copy
    selectedProperties: string[];
    setSelectedProperties: React.Dispatch<React.SetStateAction<string[]>>;

    // Target selection
    targetScreens: string[];
    setTargetScreens: React.Dispatch<React.SetStateAction<string[]>>;
    targetElements: string[];
    setTargetElements: React.Dispatch<React.SetStateAction<string[]>>;

    // Dialog state
    showReplicateDialog: boolean;
    setShowReplicateDialog: (show: boolean) => void;

    // Source selection (what we're copying from)
    sourceScreenId: string | null;
    setSourceScreenId: (screenId: string | null) => void;
    sourceElementId: string | null;
    setSourceElementId: (elementId: string | null) => void;

    // Helper functions
    toggleProperty: (property: string) => void;
    clearSelection: () => void;
    isPropertySelected: (property: string) => boolean;
}

const ReplicateContext = createContext<ReplicateContextType | null>(null);

export const useReplicate = () => {
    const context = useContext(ReplicateContext);
    if (!context) {
        throw new Error('useReplicate must be used within a ReplicateProvider');
    }
    return context;
};

interface ReplicateProviderProps {
    children: ReactNode;
}

export const ReplicateProvider: React.FC<ReplicateProviderProps> = ({
    children,
}) => {
    const [replicateMode, setReplicateMode] = useState(false);
    const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
    const [targetScreens, setTargetScreens] = useState<string[]>([]);
    const [targetElements, setTargetElements] = useState<string[]>([]);
    const [showReplicateDialog, setShowReplicateDialog] = useState(false);
    const [sourceScreenId, setSourceScreenId] = useState<string | null>(null);
    const [sourceElementId, setSourceElementId] = useState<string | null>(null);

    const toggleProperty = (property: string) => {
        setSelectedProperties((prev: string[]) =>
            prev.includes(property)
                ? prev.filter((p: string) => p !== property)
                : [...prev, property],
        );
    };

    const clearSelection = () => {
        setSelectedProperties([]);
        setTargetScreens([]);
        setTargetElements([]);
        setSourceScreenId(null);
        setSourceElementId(null);
    };

    const isPropertySelected = (property: string) => {
        return selectedProperties.includes(property);
    };

    const value: ReplicateContextType = {
        replicateMode,
        setReplicateMode,
        selectedProperties,
        setSelectedProperties,
        targetScreens,
        setTargetScreens,
        targetElements,
        setTargetElements,
        showReplicateDialog,
        setShowReplicateDialog,
        sourceScreenId,
        setSourceScreenId,
        sourceElementId,
        setSourceElementId,
        toggleProperty,
        clearSelection,
        isPropertySelected,
    };

    return (
        <ReplicateContext.Provider value={value}>
            {children}
        </ReplicateContext.Provider>
    );
};
